import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './O_FeedSearchResults.module.css';
import { User } from '@/types';
import { Capsule } from '@/services/capsulesApi';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslation } from '@/constants/translations';
import { PATHS } from '@/constants/paths';
import A_UserAvatar from '@/components/A_Atoms/A_UserAvatar/A_UserAvatar';
import A_Button from '@/components/A_Atoms/A_Button/A_Button';
import cn from 'classnames';
import { useState } from 'react';
import { usersApi } from '@/services/usersApi';

interface O_FeedSearchResultsProps {
  query: string;
  users: User[];
  capsules: Capsule[];
  isLoading: boolean;
  onClose: () => void;
}

export default function O_FeedSearchResults({
  query,
  users,
  capsules,
  isLoading,
  onClose,
}: O_FeedSearchResultsProps) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = (key: string) => getTranslation(language, key);
  const [followOverrides, setFollowOverrides] = useState<
    Record<string, boolean>
  >({});
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!wrapperRef.current || !target) return;
      if (!wrapperRef.current.contains(target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const handleUserClick = (user: User) => {
    const handle = user.handle || user.id;
    navigate(PATHS.USER_PROFILE_BY_HANDLE(handle));
    onClose();
  };

  const handleCapsuleClick = (capsule: Capsule) => {
    navigate(PATHS.CAPSULE_BY_ID(capsule.id));
    onClose();
  };

  const handleFollowToggle = async (
    userId: string,
    currentlyFollowing: boolean
  ) => {
    try {
      if (currentlyFollowing) {
        await usersApi.unfollowUser(userId);
        setFollowOverrides(prev => ({ ...prev, [userId]: false }));
      } else {
        await usersApi.followUser(userId);
        setFollowOverrides(prev => ({ ...prev, [userId]: true }));
      }
    } catch {
      // ignore follow/unfollow error in search dropdown
    }
  };

  const queryTrimmed = query.trim();
  const hasResults = users.length > 0 || capsules.length > 0;

  if (!queryTrimmed) return null;

  return (
    <div className={styles.backdrop}>
      <div
        className={styles.wrapper}
        ref={wrapperRef}
        onClick={e => {
          e.stopPropagation();
        }}
      >
        {isLoading && (
          <div className={styles.loading_row}>
            <span className='text-p-7'>{t('header.searchLoading')}</span>
          </div>
        )}
        {!isLoading && !hasResults && (
          <div className={styles.empty_row}>
            <span className='text-p-7'>{t('header.searchEmpty')}</span>
          </div>
        )}

        {users.length > 0 && (
          <div className={styles.section}>
            <div className={styles.section_header}>
              <span className={cn(styles.section_title, 'text-p-7')}>
                {t('header.searchUsers')}
              </span>
            </div>
            <div className={styles.section_body}>
              {users.map(user => {
                const name =
                  user.personal.firstName || user.personal.lastName
                    ? `${user.personal.firstName ?? ''} ${
                        user.personal.lastName ?? ''
                      }`.trim()
                    : t('userProfileCard.notSpecified');
                const isFollowing =
                  followOverrides[String(user.id)] ?? Boolean(user.is_followed);

                return (
                  <button
                    key={user.id}
                    type='button'
                    className={styles.user_row}
                    onClick={() => handleUserClick(user)}
                  >
                    <div className={styles.user_main}>
                      {user.avatar ? (
                        <img
                          src={user.avatar}
                          alt={name}
                          className={styles.avatar}
                        />
                      ) : (
                        <A_UserAvatar width={28} height={28} />
                      )}
                      <div className={styles.user_texts}>
                        <span className={cn(styles.user_name, 'text-p-7')}>
                          {name}
                        </span>
                        {user.handle && (
                          <span className={cn(styles.user_handle, 'text-p-7')}>
                            @{user.handle}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className={styles.user_actions}>
                      <A_Button
                        variant={isFollowing ? 'secondary' : 'primary'}
                        size='small'
                        fontSize='text-p-4'
                        onClick={e => {
                          if (e) e.stopPropagation();
                          void handleFollowToggle(String(user.id), isFollowing);
                        }}
                      >
                        {isFollowing
                          ? t('userProfileCard.unfollow')
                          : t('userProfileCard.follow')}
                      </A_Button>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {capsules.length > 0 && (
          <div className={styles.section}>
            <div className={styles.section_header}>
              <span className={cn(styles.section_title, 'text-p-7')}>
                {t('header.searchCapsules')}
              </span>
            </div>
            <div className={styles.section_body}>
              {capsules.map(capsule => {
                const author = capsule.author;

                return (
                  <button
                    key={capsule.id}
                    type='button'
                    className={styles.capsule_row}
                    onClick={() => handleCapsuleClick(capsule)}
                  >
                    <div className={styles.capsule_main}>
                      <div className={styles.capsule_texts}>
                        <span className={cn(styles.capsule_title, 'text-p-7')}>
                          {capsule.title}
                        </span>
                        {capsule.description && (
                          <span
                            className={cn(
                              styles.capsule_description,
                              'text-p-7'
                            )}
                          >
                            {capsule.description}
                          </span>
                        )}
                        <div className={styles.capsule_author}>
                          {author.avatar ? (
                            <img
                              src={author.avatar}
                              alt={author.name}
                              className={styles.avatar_small}
                            />
                          ) : (
                            <A_UserAvatar width={20} height={20} />
                          )}
                          <span className={cn(styles.author_name, 'text-p-7')}>
                            {author.name}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
